import {
  CatPrecheckResponse,
  CatRunChange,
  CatRunChangeReason,
  CatRunChangeReasonDetail,
  CatRunChangeResult,
} from "../../../client/types.gen";
import { PerilDataSource } from "../layers/util";

type CatVendorKey = keyof Omit<CatPrecheckResponse, "warnings">;

// CAT vendor precheck slots whose results feed each peril source. Staleness in
// any of them means premium priced from that source may be out of date.
const STALE_VENDORS_BY_SOURCE = {
  air: ["icm"],
  rms: ["rms"],
  spatialkat: ["spatialkat"],
  rms_22: ["rms_22"],
  rms_22_air_50_50: ["rms_22", "icm", "spatialkat"],
} as const satisfies Record<PerilDataSource, CatVendorKey[]>;

// Single source of user-facing copy for each change trigger. ``limits`` /
// ``deductibles`` / ``layers`` each cover a whole report section, so they
// can't name the specific term that changed.
export const CAT_RUN_CHANGE_REASON_LABELS: Record<CatRunChangeReason, string> =
  {
    addresses: "Addresses",
    effective_date: "Effective date",
    expiration_date: "Expiration date",
    limits: "Limits",
    deductibles: "Deductibles",
    layers: "Layers",
    new_modelable_peril: "Newly modelable peril",
    enrichment: "Enriched data",
  };

// A reason's label, with its value delta when the backend captured one, e.g.
// "Effective date: 08/31/2026 → 09/01/2026". Deltas are only present for the
// scalar value-diff reasons (the policy dates); all others show the label.
export const reasonDetailLabel = (detail: CatRunChangeReasonDetail): string => {
  const label = CAT_RUN_CHANGE_REASON_LABELS[detail.reason];
  if (detail.before !== null && detail.after !== null) {
    return `${label}: ${detail.before} → ${detail.after}`;
  }
  return label;
};

// Comma-joined reason labels, e.g. "Limits, Deductibles". Empty string when
// there are no reasons.
const reasonsPhrase = (reasons: CatRunChangeReasonDetail[]): string =>
  reasons.map((d) => CAT_RUN_CHANGE_REASON_LABELS[d.reason]).join(", ");

// Glanceable per-vendor freshness for the model tabs: ``fresh`` (green check),
// ``warn`` (terms drifted), ``error`` (needs a full remodel), ``unrun`` (no
// complete run yet — an empty circle rather than a bare tab).
export type CatRunStatusSeverity = "fresh" | "warn" | "error" | "unrun";
export const catRunStatusSeverity = (
  change: CatRunChange
): CatRunStatusSeverity => {
  switch (change) {
    case "unchanged":
      return "fresh";
    case "terms_changed":
      return "warn";
    case "full_remodel":
      return "error";
    case "no_complete_run":
      return "unrun";
  }
};

// Headline for the per-vendor stale banner on the modeling page. The specific
// triggers are listed separately (see ``CAT_RUN_CHANGE_REASON_LABELS``). Null
// when there is nothing to surface.
export const staleRunChangeSummary = (change: CatRunChange): string | null => {
  switch (change) {
    case "full_remodel":
      return "CAT results are out of date — re-run CAT modeling.";
    case "terms_changed":
      return "CAT results are stale — re-run CAT modeling.";
    default:
      return null;
  }
};

// Reduce a set of per-vendor change results to one advisory string: stale if
// any vendor has drifted. A full re-model outranks a terms-only reprice, so its
// reasons drive the wording. Null when none are stale.
const catResultsStaleMessage = (
  results: CatRunChangeResult[]
): string | null => {
  const remodel = results.find((r) => r.change === "full_remodel");
  if (remodel) {
    return `CAT results are out of date (${reasonsPhrase(remodel.reasons)} changed since they were generated). Re-run CAT modeling.`;
  }
  const terms = results.find((r) => r.change === "terms_changed");
  if (terms) {
    return `CAT results are stale (${reasonsPhrase(terms.reasons)} changed). Re-run CAT modeling.`;
  }
  return null;
};

// Stale-results advisory reduced over only the CAT vendors behind the given
// peril sources. Null when none of those vendors has a complete run that has
// since drifted.
export const catStaleMessageForSources = (
  cat: CatPrecheckResponse,
  sources: PerilDataSource[]
): string | null => {
  const vendors = [
    ...new Set(sources.flatMap((s) => STALE_VENDORS_BY_SOURCE[s])),
  ];
  return catResultsStaleMessage(vendors.map((v) => cat[v].results_change));
};
